import { useTranslations } from '@power-core/toolkit';
import { useState } from 'react';

import type { Data } from '@strapi/types';

import { useDocumentAccessesInit } from '@/modules/document/hooks/use-document-accesses-init';
import { useDate } from '@/modules/utils/custom-hooks/use-date';
import { useSharedDocumentsApi } from './use-shared-documents-api';

export const useSharedDocumentsModalParams = ({
	projectDocumentId,
}: {
	projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
}) => {
	const { documentAccesses } = useDocumentAccessesInit({ projectDocumentId });
	const { revokeAccess, updateAccessLevel, updateAccessPeriod, isPending } = useSharedDocumentsApi(projectDocumentId);
	const t = useTranslations();
	const { format } = useDate();

	const [isNew, setIsNew] = useState(false);
	const [isDatepickerOpen, setIsDatepickerOpen] = useState<boolean>(false);

	const handleDeleteButton = (documentId: Data.Entity<'api::document-access.document-access'>['documentId']) =>
		revokeAccess.mutate({ accessDocumentId: documentId });

	const handleChangePeriod = ({
		value,
		documentId,
	}: {
		value: string | string[];
		documentId: Data.Entity<'api::document-access.document-access'>['documentId'];
	}) => {
		if (Array.isArray(value)) {
			updateAccessPeriod.mutate({
				accessDocumentId: documentId,
				body: { startsAt: format(value[0], 'YYYY-MM-DD'), expiresAt: format(value[1], 'YYYY-MM-DD'), unlimited: false },
			});
		}

		if (value === 'unlimited') {
			updateAccessPeriod.mutate({
				accessDocumentId: documentId,
				body: { expiresAt: null, startsAt: null, unlimited: true },
			});
		}
	};

	const handleChangeAccessLevel = ({
		documentId,
		value,
	}: {
		documentId: Data.Entity<'api::document-access.document-access'>['documentId'];
		value: string;
	}) => {
		if (value === 'view' || value === 'edit') {
			updateAccessLevel.mutate({ accessDocumentId: documentId, body: { newAccessLevel: value } });
		}
	};

	return {
		documentAccesses,
		isNew,
		isDatepickerOpen,
		setIsDatepickerOpen,
		isPending,
		setIsNew,
		handleDeleteButton,
		handleChangePeriod,
		handleChangeAccessLevel,
		t,
		format,
	};
};
